import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
    swFade,
    swFloatUp,
    swSlideLeft,
    swSlideRight,
    swViewport,
    swZoom,
} from "../components/software/shared/softwareMotion";
import { wapIndustries, wapIntegrations } from "../data/softwarePagesContent";
import googleLogo from "../assets/integration-logos/google.svg";
import hubspotLogo from "../assets/integration-logos/hubspot.svg";
import salesforceLogo from "../assets/integration-logos/salesforce.svg";
import slackLogo from "../assets/integration-logos/slack.svg";
import zohoLogo from "../assets/integration-logos/zoho.svg";
import "../styles/software-pages-base.css";
import "../styles/whatsapp-api-platform.css";

const integrationLogos: Record<string, string> = {
    Salesforce: salesforceLogo,
    HubSpot: hubspotLogo,
    Zoho: zohoLogo,
    Slack: slackLogo,
    "Google Sheets": googleLogo,
};

/** Shared polar layout — SVG viewBox units match node % positions (center 50, radius 38). */
const HUB_CENTER = 50;
const HUB_ORBIT_RADIUS = 38;

const getHubOrbitPosition = (index: number, total: number) => {
    const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
    return {
        x: HUB_CENTER + Math.cos(angle) * HUB_ORBIT_RADIUS,
        y: HUB_CENTER + Math.sin(angle) * HUB_ORBIT_RADIUS,
    };
};

const floatingNotes = [
    { text: "Order #4821 shipped", top: "12%", left: "8%", delay: 0 },
    { text: "OTP verified ✓", top: "28%", right: "6%", delay: 0.15 },
    { text: "Appointment confirmed", bottom: "22%", left: "10%", delay: 0.3 },
    { text: "Payment received", bottom: "10%", right: "12%", delay: 0.45 },
];

export const WhatsappApiPlatformPage = () => {
    const navigate = useNavigate();
    const [activeIndustry, setActiveIndustry] = useState(wapIndustries[0].id);
    const heroRef = React.useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const mockupY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const notesY = useTransform(scrollYProgress, [0, 1], [0, -30]);

    const industry = wapIndustries.find((i) => i.id === activeIndustry) ?? wapIndustries[0];

    return (
        <div className="sw-page wap-page">
            {/* §1 Hero with parallax mockup */}
            <section className="wap-hero" ref={heroRef}>
                <div className="sw-container wap-hero__inner">
                    <motion.div
                        className="wap-hero__copy"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swSlideLeft}
                    >
                        <h1 className="wap-hero-title">WhatsApp API Platform</h1>
                        <h2 className="wap-tagline">Enterprise messaging infrastructure on WhatsApp Business API</h2>
                        <p className="wap-lead">
                            Connect your CRM, e-commerce stack, and internal tools to send
                            transactional, conversational, and notification messages at scale.
                        </p>
                    </motion.div>

                    <div className="wap-hero__visual">
                        {floatingNotes.map((note) => (
                            <motion.div
                                key={note.text}
                                className="wap-notif"
                                style={{
                                    top: note.top,
                                    left: note.left,
                                    right: note.right,
                                    bottom: note.bottom,
                                    y: notesY,
                                }}
                                initial={{ opacity: 0, scale: 0.85 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={swViewport}
                                transition={{ delay: note.delay, duration: 0.5 }}
                            >
                                <span className="wap-notif__dot" />
                                {note.text}
                            </motion.div>
                        ))}
                        <motion.div className="wap-mockup" style={{ y: mockupY }} variants={swZoom} initial="hidden" whileInView="visible" viewport={swViewport}>
                            <div className="wap-mockup__header">
                                <span>API Console</span>
                                <em>Connected</em>
                            </div>
                            <div className="wap-mockup__body">
                                <div className="wap-mockup__endpoint">POST /v1/messages</div>
                                <pre className="wap-mockup__code">{`{
  "to": "+91XXXXXXXXXX",
  "type": "template",
  "template": "order_update"
}`}</pre>
                                <div className="wap-mockup__status">200 OK · 42ms</div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* §2 Industry tabs */}
            <section className="wap-industries">
                <div className="sw-container">
                    <motion.div
                        className="wap-section-head wap-section-head--center"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        <h2 className="wap-h2">Industry Use Cases</h2>
                        <p className="wap-sub">Purpose-built flows for the sectors that rely on WhatsApp most.</p>
                    </motion.div>
                    <div className="wap-industries__body">
                        <div className="wap-tabs" role="tablist" aria-label="Industry use cases">
                            {wapIndustries.map((ind) => (
                                <button
                                    key={ind.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeIndustry === ind.id}
                                    className={`wap-tabs__btn${activeIndustry === ind.id ? " wap-tabs__btn--active" : ""}`}
                                    onClick={() => setActiveIndustry(ind.id)}
                                >
                                    {ind.label}
                                </button>
                            ))}
                        </div>
                        <div className="wap-tab-panel-wrap">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={industry.id}
                                    className="wap-tab-panel"
                                    role="tabpanel"
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <h3 className="wap-tab-panel__headline">{industry.headline}</h3>
                                    <p className="wap-tab-panel__desc">{industry.description}</p>
                                    <ul className="wap-tab-panel__features">
                                        {industry.points.map((pt) => (
                                            <li key={pt}>{pt}</li>
                                        ))}
                                    </ul>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </section>

            {/* §3 Integration ecosystem */}
            <section className="wap-integrations">
                <div className="sw-container">
                    <motion.div
                        className="wap-section-head wap-section-head--center"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        <h2 className="wap-h2 wap-h2--light">Integration Ecosystem</h2>
                        <p className="wap-sub wap-sub--light">Plug into the tools your team already uses.</p>
                    </motion.div>
                    <div className="wap-hub" aria-label="Integration ecosystem diagram">
                        <svg
                            className="wap-hub__lines"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="xMidYMid meet"
                            aria-hidden
                        >
                            {wapIntegrations.map((_, i) => {
                                const { x, y } = getHubOrbitPosition(i, wapIntegrations.length);
                                return (
                                    <motion.line
                                        key={i}
                                        x1={HUB_CENTER}
                                        y1={HUB_CENTER}
                                        x2={x}
                                        y2={y}
                                        stroke="rgba(158, 0, 255,0.5)"
                                        strokeWidth="0.35"
                                        strokeLinecap="round"
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={swViewport}
                                        transition={{ delay: i * 0.07, duration: 0.55 }}
                                    />
                                );
                            })}
                        </svg>
                        <motion.div
                            className="wap-hub__center"
                            initial="hidden"
                            whileInView="visible"
                            viewport={swViewport}
                            variants={swZoom}
                        >
                            <span>API</span>
                        </motion.div>
                        {wapIntegrations.map((intg, i) => {
                            const { x, y } = getHubOrbitPosition(i, wapIntegrations.length);
                            const logo = integrationLogos[intg.name];
                            return (
                                <motion.div
                                    key={intg.name}
                                    className="wap-hub__node"
                                    style={{ left: `${x}%`, top: `${y}%` }}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={swViewport}
                                    variants={swFade}
                                    custom={i * 0.06}
                                >
                                    {logo ? (
                                        <img src={logo} alt={intg.name} />
                                    ) : (
                                        <span className="wap-hub__abbr">{intg.abbr}</span>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="sw-cta">
                <div className="sw-container">
                    <div className="sw-cta__inner">
                        <h2 className="sw-cta__title">Build on WhatsApp Business API</h2>
                        <p className="sw-cta__desc">Webhooks, templates, and sandbox access — ready for your product team.</p>
                        <Button className="sw-btn" onClick={() => navigate("/contact")}>
                            Talk to API Team <ArrowRightOutlined />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};
